import { useEffect, useState, ChangeEvent } from 'react';
import { Select, FormHelperText, FormControl, MenuItem, InputLabel } from '@material-ui/core';
import { SelectProps } from '@material-ui/core/Select/Select';
import { generateUniqueId } from '../../shared/helpers/helpers';
import { useStyles } from './select-field.styles';

interface IDataSource {
    text: string;
    value: string;
}

interface ISelectFieldEventFunction {
    (v: IDataSource): void;
}

type SelectFieldProps = {
    /**
     * Array of objects to render as the options of the field
     */
    data: Array<IDataSource>;
    /**
     * The html label of the field
     */
    label?: string;
    /**
     * If true, the input element will be required
     */
    required?: boolean;
    /**
     * If true, the input element will be disabled
     */
    disabled?: boolean;
    /**
     * Triggers the error state of the field
     */
    error?: boolean;
    /**
     * The text that is shown to the user when the field is in error state
     */
    errorText?: string;
    /**
     * Function that will recieve the data from the field
     */
    fieldEvent: ISelectFieldEventFunction;
    /**
     * If true, the field will have the same width as its container
     */
    fullWidth?: boolean;
    /**
     * The currently selected option
     */
    selectedValue?: IDataSource;
} & SelectProps;

const SelectField = ({
    label,
    data = [],
    required,
    disabled,
    error = false,
    errorText,
    fieldEvent,
    fullWidth = true,
    selectedValue,
    ...rest
}: SelectFieldProps) => {
    const classes = useStyles();
    const [selected, setSelected] = useState<IDataSource>(selectedValue || { value: '', text: '' });
    const labelId = `flos-select-label-${generateUniqueId()}`;
    const selectMenuId = `flos-select-menu-${generateUniqueId()}`;

    function handleOnChange(event: ChangeEvent<{ value: unknown }>) {
        data.forEach((v: IDataSource) => {
            if (event && event.target.value === v.value) {
                setSelected(v);
                fieldEvent(v);
            }
        });
    }

    useEffect(() => {
        if (selectedValue) {
            setSelected(selectedValue);
        } else {
            setSelected({ value: '', text: '' });
        }
    }, [selectedValue]);

    const triggerIcon = () => (
        <>
            <svg
                className="MuiSvgIcon-root MuiSelect-icon"
                xmlns="http://www.w3.org/2000/svg"
                width="15.061"
                height="8.591"
                viewBox="0 0 15.061 8.591"
            >
                <path
                    d="M202.6-202.618l7,7,7-7"
                    transform="translate(-202.066 203.148)"
                    className={error ? classes.errorIcon : classes.icon}
                />
            </svg>
        </>
    );

    /**
     * Blur element when click outside
     * Avoids label to stick as if value is selected
     */
    const onExitedRegionSelect = (): void => {
        (document.activeElement as HTMLElement).blur();
    };

    return (
        <div className={classes.wrapper}>
            <FormControl fullWidth={fullWidth} className={label && classes.selectMenu} error={error}>
                {label && (
                    <InputLabel id={labelId} htmlFor={selectMenuId} disabled={disabled}>
                        {label}
                    </InputLabel>
                )}
                <Select
                    disabled={disabled}
                    labelId={labelId}
                    id={selectMenuId}
                    value={data?.length ? selected.value : ''}
                    onChange={handleOnChange}
                    required={required}
                    inputProps={{
                        className: classes.selectMenuContent,
                    }}
                    IconComponent={triggerIcon}
                    MenuProps={{
                        className: classes.selectMenuItems,
                        anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'left',
                        },
                        transformOrigin: {
                            vertical: 'top',
                            horizontal: 'left',
                        },
                        getContentAnchorEl: null,
                        elevation: 0,
                        marginThreshold: 5,
                        PaperProps: {
                            square: true,
                        },
                        TransitionProps: { onExited: onExitedRegionSelect },
                    }}
                    {...rest}
                >
                    {data.map((value: IDataSource, index: number) => {
                        return (
                            <MenuItem key={`select-menu-${index}`} value={value.value}>
                                {value.text}
                            </MenuItem>
                        );
                    })}
                </Select>
                <FormHelperText className={error ? 'error' : 'no-error'}>{error && errorText}</FormHelperText>
            </FormControl>
        </div>
    );
};

export { SelectField };
export type { SelectFieldProps };
export type { IDataSource };
export type { ISelectFieldEventFunction };
